/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  ApprovalStatusLabel,
  convertApprovalStatus,
} from '../../../../../../../api/enumerations';
import { listNoticeCompanies } from '../../../../../../../api/theHive/notices';
import {
  ListNoticeCompaniesParams,
  NoticeCompaniesData,
} from '../../../../../../../api/theHive/notices/types';
import { Constants } from '../../../../../../../constants/hiveNotices';
import {
  IconCloseMS,
  IconSearchMS,
} from '../../../../../../../constants/icons';
import { formatDate } from '../../../../../../../helpers';
import { useDebounce } from '../../../../../../../hooks/useDebounce';
import useErrorMessage from '../../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../../hooks/useSnackbar';
import { CommentsCell } from '../../../components/CompanyComments/CommentsCell';
import { CommentsDrawer } from '../../../components/CompanyComments/CommentsDrawer';
import {
  ClearSearchButton,
  SearchInput,
  StyledCell,
  StyledPagination,
  StyledRow,
  TableCellHeader,
} from '../styles';
import { AnalysisStatusIcon } from './AnalysisStatusIcon';

export function RequestList(): JSX.Element {
  const [licenseRequest, setLicenseRequest] = useState<NoticeCompaniesData[]>(
    []
  );
  const [commentsTarget, setCommentsTarget] =
    useState<NoticeCompaniesData | null>(null);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [licenseRequestTotalPages, setLicenseRequestTotalPages] = useState(0);
  const licenseRequestPerPage = 10;

  const { handleSnackbar } = useSnackbar();
  const { notice: noticeIdParam } = useParams();
  const noticeId = parseInt(noticeIdParam as string, 10);
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();

  // Page and search live in the URL to survive returning from the analysis.
  const [searchParams, setSearchParams] = useSearchParams();
  const pageLicenseRequest = Math.max(
    1,
    parseInt(searchParams.get('page') || '1', 10) || 1
  );
  const searchFromUrl = searchParams.get('search') || '';

  const [searchTerm, setSearchTerm] = useState(searchFromUrl);
  const debouncedSearch = useDebounce(searchTerm);

  const applySearchToUrl = (value: string): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set('search', value);
        else next.delete('search');
        // A new search always resets pagination.
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (debouncedSearch !== searchFromUrl) applySearchToUrl(debouncedSearch);
  }, [debouncedSearch]);

  // Clearing is explicit, so it skips the debounce.
  const handleClearSearch = (): void => {
    setSearchTerm('');
    applySearchToUrl('');
  };

  const handleNavigate = (companyId: number): void => {
    navigate(`./request/${companyId}`);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(value));
      return next;
    });
  };

  const handleLicenseRequest = useCallback(async (): Promise<void> => {
    setLoadingRequest(true);
    try {
      const params: ListNoticeCompaniesParams = {
        publicNoticeId: noticeId,
        page: pageLicenseRequest,
        size: licenseRequestPerPage,
        search: searchFromUrl,
        approvalStatus: [
          ApprovalStatusLabel.PENDING,
          ApprovalStatusLabel.AWAITING_FORMALIZATION,
          ApprovalStatusLabel.FORMALIZATION_SUBMITTED,
        ],
      };
      const response = await listNoticeCompanies(params);

      if (response instanceof Error) throw response;

      if (response.detail.description)
        throw new Error(response.detail.description);

      if (!response.data) throw new Error('Algo deu errado, tente novamente.');

      setLicenseRequest(response.data);
      setLicenseRequestTotalPages(response.detail.total_pages);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setLoadingRequest(false);
    }
  }, [noticeId, pageLicenseRequest, searchFromUrl]);

  useEffect(() => {
    if (noticeId) {
      handleLicenseRequest();
    }
  }, [handleLicenseRequest]);

  // Nothing to search with zero requests; stays enabled while a search is
  // active so the filter can be cleared.
  const searchDisabled =
    !searchFromUrl && !loadingRequest && licenseRequest.length === 0;

  const renderRows = (): JSX.Element => {
    if (loadingRequest) {
      return (
        <StyledRow>
          <StyledCell colSpan={6} align="center">
            <CircularProgress size={24} />
          </StyledCell>
        </StyledRow>
      );
    }

    if (licenseRequest.length === 0) {
      return (
        <StyledRow>
          <StyledCell colSpan={6} align="center">
            {searchFromUrl
              ? Constants.noRequestsFound
              : Constants.noRequestsRegistered}
          </StyledCell>
        </StyledRow>
      );
    }

    return (
      <>
        {licenseRequest.map((item) => (
          <StyledRow
            key={item.notice_company_id}
            onClick={() => handleNavigate(item.notice_company_id)}
          >
            <StyledCell>{item.licensed_company.company.name}</StyledCell>
            <StyledCell align="center">
              {item.requested_at ? formatDate(item.requested_at) : '—'}
            </StyledCell>
            <StyledCell align="center">
              {item.licensed_company.company.responsible_user?.name}
            </StyledCell>
            <StyledCell align="center">
              {convertApprovalStatus(item.approval_status)}
            </StyledCell>
            <StyledCell align="center">
              <AnalysisStatusIcon
                status={item.administrativo_status ?? 'na'}
                axis="contract"
                submittedAt={item.formalization_submitted_at}
              />
            </StyledCell>
            <StyledCell align="center">
              <CommentsCell
                count={item.comments_count ?? 0}
                disabled={false}
                onOpen={() => setCommentsTarget(item)}
              />
            </StyledCell>
          </StyledRow>
        ))}
      </>
    );
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <SearchInput
          placeholder={Constants.searchCompany}
          inputProps={{ 'aria-label': 'pesquisar', maxLength: 60 }}
          value={searchTerm}
          disabled={searchDisabled}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={
            <InputAdornment position="start">{IconSearchMS}</InputAdornment>
          }
          endAdornment={
            searchTerm ? (
              <InputAdornment position="end">
                <ClearSearchButton
                  aria-label={Constants.clearSearch}
                  onClick={handleClearSearch}
                >
                  {IconCloseMS}
                </ClearSearchButton>
              </InputAdornment>
            ) : null
          }
        />
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader width="22%">Nome da empresa</TableCellHeader>
              <TableCellHeader align="center" width="19%">
                Data da solicitação
              </TableCellHeader>
              <TableCellHeader align="center" width="19%">
                Responsável
              </TableCellHeader>
              <TableCellHeader align="center" width="20%">
                Status
              </TableCellHeader>
              <TableCellHeader align="center" width="18%">
                Administrativo
              </TableCellHeader>
              <TableCellHeader align="center" width="7%">
                Notas
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
      <StyledPagination
        page={pageLicenseRequest}
        count={licenseRequestTotalPages}
        onChange={handlePageChange}
      />
      <CommentsDrawer
        open={!!commentsTarget}
        onClose={() => setCommentsTarget(null)}
        noticeId={noticeId}
        licensedCompanyId={commentsTarget?.licensed_company_id}
        companyName={commentsTarget?.licensed_company.company.name ?? ''}
      />
    </>
  );
}
